import get from 'lodash.get';
import { useRef, useEffect, FC } from 'react';
import { useFragment, graphql } from 'react-relay';
import { setUpPanesAction } from '../../../actions/checkoutSetUpActions';
import { newQuotesRedirectBuyerAction } from '../../../actions/checkoutDeliveryActions';
import {
    deliveryOptionsComputing,
    updateDeliveryOptions,
} from '../../../actions/checkoutUIActions';
import { getDefaultCountry } from '../../../helpers/checkoutForms';
import { filterFalsy } from 'dibs-ts-utils/exports/filterFalsy';
import { filterQuotes } from '../../../helpers/filterQuotes';

// components
import CheckoutTransactionQueryRenderer from './CheckoutTransactionQueryRenderer';
import { checkoutDeliveryOptionsQuery } from '../../../queries/checkoutDeliveryOptions';
import {
    checkoutDeliveryOptionsQuery$data,
    checkoutDeliveryOptionsQuery as checkoutDeliveryOptionsQueryType,
} from '../../../queries/__generated__/checkoutDeliveryOptionsQuery.graphql';
import { usePreloadedDuties } from '../../../delivery/usePreloadedDuties';

import { usePrevious } from 'dibs-react-hooks/exports/usePrevious';
import { useCheckoutSelector, useAppDispatch } from '../../../reducers/root';

import { useRendererQueryLoader } from './useRendererQueryLoader';

import { CheckoutDeliveryOptionsQueryRenderer_buyer$key } from './__generated__/CheckoutDeliveryOptionsQueryRenderer_buyer.graphql';
import { CheckoutDeliveryOptionsQueryRenderer_preCheckoutTransaction$key } from './__generated__/CheckoutDeliveryOptionsQueryRenderer_preCheckoutTransaction.graphql';
import { CheckoutDeliveryOptionsQueryRenderer_item$key } from './__generated__/CheckoutDeliveryOptionsQueryRenderer_item.graphql';
import { CheckoutDeliveryOptionsQueryRenderer_viewer$key } from './__generated__/CheckoutDeliveryOptionsQueryRenderer_viewer.graphql';
import { RelayNode } from 'dibs-ts-utils/exports/RelayNode';
import { getFdfQuoteABTestVariant } from 'dibs-buyer-layout/src/helpers/getFdfQuoteABTestVariant';
import { useCheckoutServerVars } from '../../shared/CheckoutServerVarsContext';

const canUseItemQuote = (
    quote: ItemDeliveryOptionsNode,
    transactionQuotes: TransactionDeliveryOptions['edges']
): boolean => {
    // You can only use an item quote if it was not already copied to a transaction quote
    // (itemQuote.serviceId is not somewhere as a transactionQuote.sourceId)
    return !transactionQuotes?.find(transactionQuote => {
        return String(quote?.serviceId) === String(transactionQuote?.node?.sourceId);
    });
};

type ItemDeliveryOptions = NonNullable<
    checkoutDeliveryOptionsQueryType['response']['viewer']['itemDeliveryOptions']
>;

type TransactionDeliveryOptions = NonNullable<
    checkoutDeliveryOptionsQueryType['response']['viewer']['transactionDeliveryOptions']
>;
type ItemDeliveryOptionsNode = NonNullable<RelayNode<ItemDeliveryOptions>>;
type TransactionDeliveryOptionsNode = NonNullable<RelayNode<TransactionDeliveryOptions>>;

type DeliveryOptions = (ItemDeliveryOptionsNode & TransactionDeliveryOptionsNode)[];
const getDeliveryOptions = (
    props?: checkoutDeliveryOptionsQueryType['response'] | null
): DeliveryOptions => {
    // Adds any requested quotes as the first quote in the list.
    // If exist, prefer transaction's pre-quotes over item's pre-quotes.
    // Sort quote's order by price
    const transactionOptions = props?.viewer?.transactionDeliveryOptions?.edges || [];
    const itemDeliveryOptions = props?.viewer?.itemDeliveryOptions?.edges || [];

    return transactionOptions
        .concat(
            itemDeliveryOptions.filter(filterFalsy).filter(({ node }) => {
                if (!node) {
                    return false;
                }
                return canUseItemQuote(node, transactionOptions);
            })
        )
        .map(edge => edge?.node)
        .filter(filterFalsy)
        .sort((q1, q2) => get(q1, 'totalAmount.amount') - get(q2, 'totalAmount.amount'));
};

function useDeliveryOptions(data: checkoutDeliveryOptionsQuery$data | null): DeliveryOptions {
    const previousData = usePrevious(data);
    const deliveryOptions = useRef<DeliveryOptions>([]);

    /**
     * Make sure the `deliveryOptions` reference changes when we have new data.
     */
    if (previousData !== data) {
        deliveryOptions.current = getDeliveryOptions(data);
    }

    return deliveryOptions.current;
}

const viewerFragment = graphql`
    fragment CheckoutDeliveryOptionsQueryRenderer_viewer on Viewer {
        ...CheckoutTransactionQueryRenderer_viewer
        ...checkoutRelayAction_viewer
        ...usePreloadedDuties_viewer
        ...checkoutSetUpActionsSetUpPanes_viewer
        userTokenCanCheckout(userId: $buyerId) @include(if: $hasBuyerId)
    }
`;
const itemFragment = graphql`
    fragment CheckoutDeliveryOptionsQueryRenderer_item on Item {
        serviceId
        ...CheckoutTransactionQueryRenderer_item
        ...checkoutRelayAction_item
        ...usePreloadedDuties_item
    }
`;
const buyerFragment = graphql`
    fragment CheckoutDeliveryOptionsQueryRenderer_buyer on User {
        ...CheckoutTransactionQueryRenderer_buyer
        ...checkoutRelayAction_buyer
        ...checkoutUIActions_buyer
        ...checkoutSetUpActions_buyer
        ...checkoutSetUpActionsSetUpPanes_buyer
        ...usePreloadedDuties_buyer
        serviceId
        addressBook(partialAddressCheckoutEligibility: $partialAddressCheckoutEligibility) {
            addressList {
                serviceId
                country
                city
                state
                zipCode
            }
        }
    }
`;
const preCheckoutTransactionFragment = graphql`
    fragment CheckoutDeliveryOptionsQueryRenderer_preCheckoutTransaction on Transaction {
        ...CheckoutTransactionQueryRenderer_preCheckoutTransaction
        ...checkoutRelayAction_preCheckoutTransaction
        ...checkoutUIActions_preCheckoutTransaction
        ...checkoutSetUpActionsSetUpPanes_preCheckoutTransaction
    }
`;

type Props = {
    viewer: CheckoutDeliveryOptionsQueryRenderer_viewer$key | null;
    item: CheckoutDeliveryOptionsQueryRenderer_item$key | null;
    buyer: CheckoutDeliveryOptionsQueryRenderer_buyer$key | null;
    preCheckoutTransaction: CheckoutDeliveryOptionsQueryRenderer_preCheckoutTransaction$key | null;
};

const CheckoutDeliveryOptionsQueryRenderer: FC<Props> = props => {
    const {
        viewer: viewerRef,
        item: itemRef,
        buyer: buyerRef,
        preCheckoutTransaction: preCheckoutTransactionRef,
    } = props;

    const viewer = useFragment(viewerFragment, viewerRef);
    const item = useFragment(itemFragment, itemRef);
    const buyer = useFragment(buyerFragment, buyerRef);
    const preCheckoutTransaction = useFragment(
        preCheckoutTransactionFragment,
        preCheckoutTransactionRef
    );

    const itemId = item?.serviceId;
    const hasSetUpPaneStore = useCheckoutSelector(state => state.pane.isSetUp);
    const transactionId = useCheckoutSelector(state => state.transaction.transactionId);
    const isReadyAfterItemBuyerFetch = useCheckoutSelector(
        state => state.ui.isReadyAfterItemBuyerFetch
    );
    const currencyConversionRateEventId = useCheckoutSelector(
        state => state.transaction.currencyConversionRateEventId
    );
    const shippingAddressId = useCheckoutSelector(state => state.transaction.shippingAddressId);
    const quantity = useCheckoutSelector(state => state.transaction.quantity);

    const shippingAddress = (buyer?.addressBook?.addressList || []).find(
        address => address?.serviceId === shippingAddressId
    );

    const [fetchQuery, data] = useRendererQueryLoader<checkoutDeliveryOptionsQueryType>(
        checkoutDeliveryOptionsQuery
    );
    const dispatch = useAppDispatch();
    const country = shippingAddress?.country || getDefaultCountry();

    const serviceId = shippingAddress?.serviceId;
    const city = shippingAddress?.city;
    const state = shippingAddress?.state;
    const zipCode = shippingAddress?.zipCode;
    const userTokenCanCheckout = viewer?.userTokenCanCheckout;
    const fdfQuoteABTestVariant = getFdfQuoteABTestVariant();

    useEffect(() => {
        /**
         * Stop checkout load if the user is not allowed to checkout. The most basic
         * use case is users with stale tokens. The expectation is that the user will login, we will
         * refetch user data and `userTokenCanCheckout` will be true.
         * Also wait until item buyer data is fetched and populated into the redux store
         */
        if (itemId && isReadyAfterItemBuyerFetch && userTokenCanCheckout !== false) {
            dispatch(deliveryOptionsComputing());
            fetchQuery({
                transactionId,
                itemId,
                country,
                city,
                state,
                zipCode,
                currencyConversionRateEventId: currencyConversionRateEventId || '',
                shouldFetchItemDeliveryOptions: !!zipCode,
                shouldFetchTransactionDeliveryOptions: !!(zipCode && transactionId),
                quantity,
                fdfQuoteABTestVariant,
            });
        }
    }, [
        currencyConversionRateEventId,
        fetchQuery,
        itemId,
        quantity,
        transactionId,
        serviceId,
        dispatch,
        country,
        city,
        state,
        zipCode,
        userTokenCanCheckout,
        fdfQuoteABTestVariant,
        isReadyAfterItemBuyerFetch,
    ]);

    const previousData = usePrevious(data);
    const deliveryOptions = useDeliveryOptions(data);
    const { checkoutType } = useCheckoutServerVars();

    useEffect(() => {
        if (data && previousData !== data) {
            dispatch(
                updateDeliveryOptions({
                    buyer,
                    deliveryOptions,
                    preCheckoutTransaction,
                })
            );
        }
    }, [buyer, data, deliveryOptions, dispatch, preCheckoutTransaction, previousData, viewer]);

    useEffect(() => {
        if (!hasSetUpPaneStore && deliveryOptions && data && previousData !== data) {
            dispatch(
                setUpPanesAction({
                    buyer,
                    deliveryOptions,
                    preCheckoutTransaction,
                    viewer,
                    checkoutType,
                })
            );
        }
    }, [
        buyer,
        checkoutType,
        data,
        deliveryOptions,
        dispatch,
        hasSetUpPaneStore,
        preCheckoutTransaction,
        previousData,
        viewer,
    ]);

    useEffect(() => {
        if (previousData && data) {
            const previousDeliveryOptions = getDeliveryOptions(previousData);

            const prevDeliveryOptions = filterQuotes(previousDeliveryOptions);
            const newDeliveryOptions = filterQuotes(deliveryOptions);
            dispatch(newQuotesRedirectBuyerAction({ prevDeliveryOptions, newDeliveryOptions }));
        }
    }, [data, previousData, dispatch, deliveryOptions]);

    const preloadedDuties = usePreloadedDuties({
        buyer,
        item,
        viewer,
        deliveryOptions,
    });

    const shippingAddressIdRef = useRef(shippingAddressId);

    if (data) {
        shippingAddressIdRef.current = shippingAddressId;
    }

    return (
        <CheckoutTransactionQueryRenderer
            {...{
                viewer,
                item,
                buyer,
                preCheckoutTransaction: preCheckoutTransaction || null,
                deliveryOptions,
                preloadedDuties,
            }}
        />
    );
};

export default CheckoutDeliveryOptionsQueryRenderer;
